import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Download, Mail, FileText, Briefcase, MapPin,
  Calendar, ExternalLink, Copy, Check, Clock
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  saved: { label: "Saved", color: "#64748b", bg: "rgba(100,116,139,0.12)" },
  applied: { label: "Applied", color: "#6366f1", bg: "rgba(99,102,241,0.12)" },
  interviewed: { label: "Interviewed", color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
  shortlisted: { label: "Shortlisted", color: "#8b5cf6", bg: "rgba(139,92,246,0.12)" },
  offer_received: { label: "Offer Received", color: "#10b981", bg: "rgba(16,185,129,0.12)" },
  rejected: { label: "Rejected", color: "#ef4444", bg: "rgba(239,68,68,0.12)" },
  rtr_submitted: { label: "RTR Submitted", color: "#0ea5e9", bg: "rgba(14,165,233,0.12)" },
};

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button className="btn-icon" onClick={handle} title="Copy" data-testid="copy-btn">
      {copied ? <Check size={14} /> : <Copy size={14} />}
    </button>
  );
}

export default function ApplicationDetailPage() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/applications/${appId}/details`);
        setData(res.data);
      } catch {
        toast.error("Failed to load application details");
        navigate("/app/tracker");
      } finally { setLoading(false); }
    })();
  }, [appId, navigate]);

  if (loading) return <div style={{ textAlign: "center", padding: "4rem" }}><div className="processing-spinner" /></div>;
  if (!data) return null;

  const st = STATUS_MAP[data.status] || STATUS_MAP.saved;
  const art = data.artifacts;

  const formatDate = (iso) => {
    if (!iso) return "-";
    try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
    catch { return iso; }
  };

  return (
    <div className="page-container fade-in" data-testid="app-detail-page">
      <button className="btn-text" onClick={() => navigate("/app/tracker")} data-testid="back-to-tracker">
        <ArrowLeft size={16} /> Back to Job Tracker
      </button>

      <div className="ad-header" data-testid="ad-header">
        <div className="ad-header-left">
          <h1 data-testid="ad-job-title">{data.job_title}</h1>
          <p className="ad-company">
            {data.company}
            {data.location && <span className="ad-location"><MapPin size={13} /> {data.location}</span>}
            {data.platform && <span className="ad-platform">{data.platform}</span>}
          </p>
        </div>
        <span className="ad-status-pill" style={{ color: st.color, background: st.bg }} data-testid="ad-status">
          {st.label}
        </span>
      </div>

      <div className="ad-meta-row" data-testid="ad-meta">
        {data.date_saved && <span className="ad-meta-item"><Calendar size={13} /> Saved: {formatDate(data.date_saved)}</span>}
        {data.applied_date && <span className="ad-meta-item"><Clock size={13} /> Applied: {formatDate(data.applied_date)}</span>}
        {data.url && <a href={data.url} target="_blank" rel="noopener noreferrer" className="ad-meta-link" data-testid="ad-job-url"><ExternalLink size={13} /> Job Posting</a>}
        {data.recruiter_email && (
          <a href={`mailto:${data.recruiter_email}`} className="ad-meta-link" data-testid="ad-recruiter-email">
            <Mail size={13} /> {data.recruiter_email}
          </a>
        )}
      </div>

      {data.notes && (
        <div className="ad-notes" data-testid="ad-notes">
          <strong>Notes:</strong> {data.notes}
        </div>
      )}

      {art ? (
        <div className="ad-artifacts" data-testid="ad-artifacts">
          <h2 className="ad-section-title">Application Artifacts</h2>
          <p className="ad-section-sub">Everything generated for this application is saved here.</p>

          <div className="ad-artifact-grid">
            {/* Resume Card */}
            <div className="ad-card" data-testid="ad-resume-card">
              <div className="ad-card-header">
                <FileText size={18} />
                <h3>Optimized Resume</h3>
                {art.has_download && (
                  <a href={`${API}/download/${art.optimization_id}`} className="ad-card-action" data-testid="ad-download-resume">
                    <Download size={14} /> Download
                  </a>
                )}
              </div>
              <div className="ad-card-meta">
                <span>{art.original_filename}</span>
                {art.ats_score != null && <span className="ad-ats-score">ATS: {typeof art.ats_score === "object" ? art.ats_score.overall_score : art.ats_score}%</span>}
                {art.format_mode && <span className="ad-format-badge">{art.format_mode === "safe_template" ? "Template" : "Preserved"}</span>}
              </div>
              {art.optimized_text && (
                <pre className="ad-card-preview" data-testid="ad-resume-preview">{art.optimized_text.slice(0, 600)}{art.optimized_text.length > 600 ? "..." : ""}</pre>
              )}
            </div>

            {/* Email Card */}
            {art.email_body && (
              <div className="ad-card" data-testid="ad-email-card">
                <div className="ad-card-header">
                  <Mail size={18} />
                  <h3>Recruiter Email</h3>
                  <CopyBtn text={art.email_body} />
                </div>
                <pre className="ad-card-preview" data-testid="ad-email-preview">{art.email_body}</pre>
              </div>
            )}

            {/* Cover Letter Card */}
            {art.cover_letter && (
              <div className="ad-card" data-testid="ad-cover-letter-card">
                <div className="ad-card-header">
                  <FileText size={18} />
                  <h3>Cover Letter</h3>
                  <CopyBtn text={art.cover_letter} />
                </div>
                <pre className="ad-card-preview" data-testid="ad-cover-letter-preview">{art.cover_letter}</pre>
              </div>
            )}

            {/* JD Card */}
            {art.job_description && (
              <div className="ad-card" data-testid="ad-jd-card">
                <div className="ad-card-header">
                  <Briefcase size={18} />
                  <h3>Job Description Used</h3>
                  <CopyBtn text={art.job_description} />
                </div>
                <pre className="ad-card-preview" data-testid="ad-jd-preview">{art.job_description.slice(0, 800)}{art.job_description.length > 800 ? "..." : ""}</pre>
              </div>
            )}
          </div>

          <div className="ad-artifact-meta" data-testid="ad-artifact-meta">
            Generated on {formatDate(art.created_at)}
            {art.deep_optimized && " | Deep Optimized"}
          </div>
        </div>
      ) : (
        <div className="ad-no-artifacts" data-testid="ad-no-artifacts">
          <Briefcase size={28} />
          <h3>No Optimization Linked</h3>
          <p>This application was added manually. Optimize a resume for this job to link artifacts.</p>
        </div>
      )}
    </div>
  );
}
